import { useEffect, useRef, useState } from 'react';

import { isSameDay } from 'date-fns';
import { useSelector } from 'hooks';
import { groupBy, sortBy } from 'lodash';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useTranslation } from 'react-i18next';

import {
  Absence,
  CustomDutyRow,
  CustomPlannedDuty,
  Duty,
  DutyRow,
  Illness,
  Leave,
  UnspecifiedAbsence
} from './types';
import { Interval, PlanUnit } from 'components/types';

import {
  getCellDateClasses,
  getRosterDays,
  prepareRosterForPrint
} from '../utils';
import { getHoursMinutesSeconds, getWeekInterval } from 'utils/date';
import { printContent } from 'utils/misc';

import { queries } from 'RestAPIs/rapini';

import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { Accordion } from 'components/shared';

import Header from '../Header';
import AbsencesByType from './Absence/AbsencesByType';
import DayCell from './DayCell';
import DutyCodeCell from './DutyCodeCell';
import RosterViewByDutyStyles from './RosterViewByDutyStyles';

type Props = {
  portal: 'employee' | 'roster';
  getData: (
    interval: Interval,
    selectedPlanUnits: PlanUnit[]
  ) => Promise<{
    duties: Duty[];
    absences: Absence[];
  }>;
  selectedPlanUnits: PlanUnit[];
  lastSelectedPlanUnitsIds?: (string | number)[] | null;
  onPlanUnitSelected: (selectedPlanUnits: PlanUnit[]) => void;
  isGroupedByPlanunit: boolean;
  onToggleGroupByPlanUnit: () => void;
  onToggleGroupByFunction: () => void;
  isGroupedByFunction: boolean;
  defaultInterval?: Interval;
  onRosterViewChanged?: (rosterView: 'employees' | 'duties') => void;
};

export default function RosterViewByDuty(props: Props) {
  const {
    portal,
    getData,
    selectedPlanUnits,
    lastSelectedPlanUnitsIds,
    onPlanUnitSelected,
    isGroupedByPlanunit,
    onToggleGroupByPlanUnit,
    isGroupedByFunction,
    onToggleGroupByFunction,
    defaultInterval,
    onRosterViewChanged
  } = props;
  const { t } = useTranslation('common');
  const [interval, setInterval] = useState(
    defaultInterval || getWeekInterval()
  );

  const holidays = useSelector((state) => state.holidays);
  const [rosterDays, setRosterDays] = useState(
    getRosterDays(interval, holidays)
  );
  const [duties, setDuties] = useState<Duty[]>([]);
  const [mappedDuties, setMappedDuties] = useState<Duty[]>([]);
  const [dutyRows, setDutyRows] = useState<DutyRow[]>([]);
  const [leaveEntries, setLeaveEntries] = useState<Leave[]>([]);
  const [illnessEntries, setIllnessEntries] = useState<Illness[]>([]);
  const [otherAbsenceEntries, setOtherAbsenceEntries] = useState<
    UnspecifiedAbsence[]
  >([]);
  const [customDutyRows, setCustomDutyRows] = useState<CustomDutyRow[]>([]);
  const rosterRef = useRef(document.createElement('div'));
  const { data: dutyCodes } = queries.useGetDutyCodes();

  const onIntervalChanged = (interval: Interval) => {
    setInterval(interval);
    setRosterDays(getRosterDays(interval, holidays));
  };

  useEffect(() => {
    setDuties([]);
    setLeaveEntries([]);
    setIllnessEntries([]);
    setOtherAbsenceEntries([]);
    if (selectedPlanUnits.length) {
      getData(interval, selectedPlanUnits).then(({ duties, absences }) => {
        setDuties(duties.filter((duty) => !duty.isFromDifferentCostCenter));
        setLeaveEntries(
          absences
            .filter((absence) => absence.type === 'LEAVE')
            .map((absence) => ({
              ...absence,
              type: 'LEAVE',
              isApproved: (absence as Leave).isApproved,
              note: (absence as Leave).note
            }))
        );
        setIllnessEntries(
          absences
            .filter((absence) => absence.type === 'ILLNESS')
            .map((absence) => ({
              ...absence,
              type: 'ILLNESS',
              isExternal: (absence as Illness).isExternal,
              hasOverlap: (absence as Illness).hasOverlap
            }))
        );
        setOtherAbsenceEntries(
          absences
            .filter((absence) => absence.type === 'UNSPECIFIED')
            .map((absence) => ({
              ...absence,
              type: 'UNSPECIFIED'
            }))
        );
      });
    }
  }, [interval, selectedPlanUnits]);

  useEffect(() => {
    if (dutyCodes?.length) {
      setMappedDuties(
        duties
          .filter((duty) => duty.dutyCode)
          .map((duty) => {
            let dutyCode =
              dutyCodes.find(
                ({ dutyCodeId }) => dutyCodeId === duty.dutyCode?.dutyCodeId
              ) || duty.dutyCode;
            if (dutyCode && duty.dutyCode) {
              dutyCode = {
                ...dutyCode,
                code: duty.dutyCode.code,
                colorCode: duty.dutyCode.colorCode
              };
            }
            return { ...duty, dutyCode };
          })
      );
    }
  }, [dutyCodes, duties]);

  useEffect(() => {
    let dutyRows = Object.values(groupBy(mappedDuties, 'dutyCode.id'));

    if (isGroupedByPlanunit) {
      dutyRows = dutyRows
        .map((arr) => Object.values(groupBy(arr, 'costCenter.id')))
        .flat();
    }

    if (isGroupedByFunction) {
      dutyRows = dutyRows
        .map((arr) => Object.values(groupBy(arr, 'function.id')))
        .flat();
    }

    setDutyRows(
      sortBy(
        dutyRows.map((duties) =>
          sortBy(duties, [
            (duty) => duty.isOpen,
            (duty) => (!duty.isOpen ? duty.assignedEmployee.nameInformal : ''),
            'costCenter.name',
            'function.name'
          ])
        ),
        [
          (arr) => getHoursMinutesSeconds(arr[0].start),
          (arr) => getHoursMinutesSeconds(arr[0].end),
          isGroupedByPlanunit ? (arr) => arr[0].costCenter?.name : '',
          isGroupedByFunction ? (arr) => arr[0].function?.name : ''
        ]
      )
    );

    let customDuties = duties.filter(
      (duty) => !duty.dutyCode
    ) as CustomPlannedDuty[];
    customDuties = sortBy(customDuties, [
      (duty) => getHoursMinutesSeconds(duty.start),
      (duty) => getHoursMinutesSeconds(duty.end),
      (duty) => duty.isOpen,
      (duty) => (!duty.isOpen ? duty.assignedEmployee.nameInformal : ''),
      'costCenter.name',
      'function.name'
    ]);

    let customDutyRows;
    if (isGroupedByPlanunit) {
      customDutyRows = sortBy(
        Object.values(groupBy(customDuties, 'costCenterId')),
        [(arr) => arr[0].costCenter?.name]
      );
    } else {
      customDutyRows = customDuties.length ? [customDuties] : [];
    }

    setCustomDutyRows(customDutyRows);
  }, [mappedDuties, isGroupedByFunction, isGroupedByPlanunit]);

  return (
    <RosterViewByDutyStyles ref={rosterRef}>
      <Header
        lastSelectedPlanUnitsIds={lastSelectedPlanUnitsIds}
        onPlanUnitSelected={onPlanUnitSelected}
        onPrintRoster={() => {
          printContent(prepareRosterForPrint(rosterRef.current));
        }}
        interval={interval}
        onIntervalChanged={onIntervalChanged}
        rosterDays={rosterDays}
        calendarView="week"
        rosterView="duties"
        onRosterViewChanged={onRosterViewChanged}
      >
        <FormControlLabel
          control={<Switch checked={isGroupedByPlanunit} />}
          label={`${t('groupByPlanUnit')}`}
          onChange={onToggleGroupByPlanUnit}
        />
        <FormControlLabel
          control={<Switch checked={isGroupedByFunction} />}
          label={`${t('groupByFunction')}`}
          onChange={onToggleGroupByFunction}
        />
      </Header>
      <div className="roster-body">
        <OverlayScrollbarsComponent
          className="wrap-container"
          options={{
            paddingAbsolute: true,
            overflowBehavior: {
              x: 'hidden'
            }
          }}
        >
          <Accordion disableGutters summary={t('duties')} defaultExpanded>
            <Table className="roster-grid">
              <TableBody>
                {dutyRows.map((duties) => (
                  <TableRow
                    key={duties
                      .map((d) => (d.isOpen ? d.requestId : d.dutyLineId))
                      .join('')}
                  >
                    <TableCell className="first-cell">
                      {duties.length > 0 && (
                        <DutyCodeCell
                          duties={duties}
                          isGroupedByFunction={isGroupedByFunction}
                          isGroupedByPlanunit={isGroupedByPlanunit}
                        />
                      )}
                    </TableCell>
                    {rosterDays.map(({ date, dayNr }) => (
                      <DayCell
                        key={dayNr}
                        date={date}
                        portal={portal}
                        isGroupedByPlanunit={isGroupedByPlanunit}
                        isGroupedByFunction={isGroupedByFunction}
                        duties={duties.filter((d) =>
                          isSameDay(new Date(d.date), date)
                        )}
                      />
                    ))}
                  </TableRow>
                ))}
                {customDutyRows.length > 0 && (
                  <TableRow className="no-highlight">
                    <TableCell className="first-cell border-right">
                      <h3>{t('others')}</h3>
                    </TableCell>
                    {rosterDays.map(({ date, dayNr }) => (
                      <TableCell
                        key={dayNr}
                        className={getCellDateClasses({ date }, [
                          'border-right'
                        ])}
                      />
                    ))}
                  </TableRow>
                )}
                {customDutyRows.map((entries) => (
                  <TableRow
                    key={entries.map((e) => e.dutyLineId).join()}
                    className="custom-duties-row"
                  >
                    <TableCell className="first-cell">
                      {isGroupedByPlanunit && entries[0]?.costCenter && (
                        <div className="plan-unit-name">
                          &bull; <b>{entries[0].costCenter.name}</b>
                        </div>
                      )}
                    </TableCell>
                    {rosterDays.map(({ date, dayNr }) => (
                      <DayCell
                        key={dayNr}
                        date={date}
                        portal={portal}
                        isGroupedByPlanunit={isGroupedByPlanunit}
                        isGroupedByFunction={isGroupedByFunction}
                        duties={entries.filter((d) =>
                          isSameDay(new Date(d.date), date)
                        )}
                      />
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Accordion>
          <Accordion disableGutters summary={t('absences')} defaultExpanded>
            <>
              {(leaveEntries.length > 0 ||
                illnessEntries.length > 0 ||
                otherAbsenceEntries.length > 0) && (
                <Table className="roster-grid">
                  <TableBody>
                    {leaveEntries.length > 0 && (
                      <AbsencesByType
                        type="leave"
                        absences={leaveEntries}
                        rosterDays={rosterDays}
                        interval={interval}
                      />
                    )}
                    {illnessEntries.length > 0 && (
                      <AbsencesByType
                        type="illness"
                        absences={illnessEntries}
                        rosterDays={rosterDays}
                        interval={interval}
                      />
                    )}
                    {otherAbsenceEntries.length > 0 && (
                      <AbsencesByType
                        type="unspecified"
                        absences={otherAbsenceEntries}
                        rosterDays={rosterDays}
                        interval={interval}
                      />
                    )}
                  </TableBody>
                </Table>
              )}
            </>
          </Accordion>
        </OverlayScrollbarsComponent>
      </div>
    </RosterViewByDutyStyles>
  );
}
